import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import StatChip from "../components/StatChip";
import Loader from "../components/Loader";
import {
  getAvatarColor,
  getCurrentRole,
  isActiveUser,
  isAdminRole,
  formatRole,
} from "../utils/common";
import { getTeamColors } from "../utils/badgeColors";

import {
  teamListApis,
  teamMembersApis,
  teamFlowApis,
} from "../redux/team/teamActions";
import { selectTeams, selectTeamsStatus } from "../redux/team/teamSelectors";
import { usersApis } from "../redux/users/userActions";
import { selectUsers } from "../redux/users/userSelectors";
import "../assets/css/teamTree.css";


const getInitials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return String(name || "?").slice(0, 2).toUpperCase();
};

/**
 * One card in the chart. The avatar hangs above it and its ring takes the
 * card's accent, so a person still reads as belonging to their team once the
 * chart is scrolled far enough that the team heading is off screen.
 */
/* `variant="team"` keeps a team's buttons on screen at all times. Member cards
   stay hover-only: their action is a remove, and a grid of crosses invites the
   accident. A team's actions are add-somebody and reorder — nothing there can
   lose data, and hiding the main thing this page is FOR made it unfindable. */
const OrgCard = ({ accent, avatarColor, icon, title, sub, meta, actions, twisty, variant }) => (
  <div
    className={`org-card${variant ? ` org-card--${variant}` : ""}`}
    style={{ "--org-accent": accent }}
  >
    {actions}
    <div
      className="org-avatar"
      style={{ backgroundColor: avatarColor || accent }}
    >
      {icon ? <i className={`bi ${icon}`} /> : getInitials(title)}
    </div>
    <div className="org-card__top">
      <div className="org-card__title" title={title}>
        {title}
      </div>
      {sub && (
        <div className="org-card__sub" title={sub}>
          {sub}
        </div>
      )}
      {meta && <div className="org-card__meta">{meta}</div>}
    </div>
    {twisty}
  </div>
);

/**
 * Teams as an org chart. A picture, and only a picture.
 *
 * It used to create teams, add and remove people and reorder as well. Every
 * one of those controls lived inside a canvas that pans sideways — a form
 * opening in the middle of a centred tree shoves every other card out from
 * under the cursor, and the buttons themselves scrolled off screen — so the
 * page that owned the edits was the page they could not be performed on.
 *
 * They live on Teams now: a plain grid of cards, one per team, with the roster
 * behind each card's People button. Reading the shape of the organisation and
 * changing it are different jobs, and only one of them wants a chart.
 *
 * What stays here is what a chart is good at: rank order inside a team, how
 * loaded each one is, and the onward routes. Those ride on each team's card
 * rather than as edges in the tree — they are lateral (Database feeds Web App,
 * and Support takes returns from everywhere), so drawing them as branches
 * would say the wrong thing about the hierarchy.
 */
const TeamTree = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const role = getCurrentRole();


  const teams = useSelector(selectTeams);
  const teamsStatus = useSelector(selectTeamsStatus);
  const membersByTeam = useSelector((s) => s.team.membersByTeam);
  const flowByTeam = useSelector((s) => s.team.flowByTeam);
  const saveStatus = useSelector((s) => s.team.memberSaveStatus);
  const usersData = useSelector(selectUsers);

  const [collapsed, setCollapsed] = useState({});
  const [busy] = useState(false);

  /* Which teams the chart draws: "all", or one TeamCode.
     With four teams the whole tree already needs sideways scrolling to read;
     by six it is unusable. Narrowing to one team is the difference between
     scanning a roster and hunting for it. */
  const [teamFilter, setTeamFilter] = useState("all");



  const activeTeams = useMemo(
    () => (teams || []).filter((t) => t.Status !== 0),
    [teams],
  );

  /* What actually renders. The counts in the header stay on activeTeams: they
     describe the organisation, and a "Teams 1" chip because somebody filtered
     the view would be a lie about the company. */
  const shownTeams = useMemo(
    () =>
      teamFilter === "all"
        ? activeTeams
        : activeTeams.filter((t) => String(t.TeamCode) === String(teamFilter)),
    [activeTeams, teamFilter],
  );

  /* A team that is filtered out but still selected would leave the chart empty
     with no clue why — so a filter pointing at a team that no longer exists
     falls back to showing everything. */
  useEffect(() => {
    if (teamFilter === "all") return;
    if (!activeTeams.some((t) => String(t.TeamCode) === String(teamFilter))) {
      setTeamFilter("all");
    }
  }, [activeTeams, teamFilter]);

  const usersList = useMemo(() => {
    const d = usersData?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    if (Array.isArray(d?.Data)) return d.Data;
    return [];
  }, [usersData]);

  useEffect(() => {
    dispatch(teamListApis({}));
    dispatch(usersApis({ currentPage: 1, pageSize: 1000 }));
  }, [dispatch]);

  /* Every team's roster and routes up front. Four teams is four small calls,
     and loading them lazily per expand made the chart pop and reflow as each
     one arrived — which in a centred layout moves every other card too. */
  useEffect(() => {
    activeTeams.forEach((t) => {
      dispatch(teamMembersApis({ teamCode: t.TeamCode }));
      dispatch(teamFlowApis({ fromTeamCode: t.TeamCode }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, activeTeams.length]);

  /* The real roster only.

     /team/members appends the all-access accounts to every team it is asked
     about, flagged IsAllTeams — right for an assignee dropdown, where you
     genuinely can hand a task to an admin. Wrong for a chart: they would
     appear under all four teams, and the count on the team card would no
     longer match tbl_UserTeam, which is what sp_Team_GetAll's MemberCount
     counts and what "who is in this team" means. They reach every team by
     role, which the footnote says once instead. */
  /* Sorted by rank, then by name. Sorted HERE rather than at the one place it
     is drawn, so the chart, the counts and anything added later all see one
     order — two lists of the same team in different orders is the kind of
     thing nobody notices until they are comparing two screens.

     A copy, because Array.prototype.sort mutates and this array belongs to the
     store: sorting it in place would reorder redux state from a render. */
  const membersOf = (teamCode) =>
    (membersByTeam[teamCode] || [])
      .filter((m) => !m.IsAllTeams)
      .slice()
      /* By POSITION, then by name. Sorting on the permission role put every
         ADMIN above every developer regardless of what either of them does,
         which is a ranking of what an account may click rather than of
         seniority. Anyone with no position sorts last: an unset field is not
         evidence either way, and putting them on top would be a claim. */
      .sort(
        (a, b) =>
          (a.DesignationRank ?? 999) - (b.DesignationRank ?? 999) ||
          String(a.UserName || "").localeCompare(String(b.UserName || "")),
      );

  const allAccess = useMemo(() => {
    const first = activeTeams[0]?.TeamCode;
    const seen = new Map();
    (membersByTeam[first] || [])
      .filter((m) => m.IsAllTeams)
      .forEach((m) => seen.set(m.UserCode, m));
    return [...seen.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeams, membersByTeam]);

  /* Who genuinely reaches every team by role, taken from the server's own
     IsAllTeams flag rather than from a role test here.

     This used to exclude anyone isAdminRole() matched — ADMIN *or*
     SUPER_ADMIN — and that is stricter than the server. sp_TeamMember_GetAll
     appends only SUPER_ADMINs as all-access; an ADMIN with a real
     tbl_UserTeam row comes back as an ordinary member, flagged
     IsAllTeams = false. Mano is exactly that: ADMIN, and the primary member
     of Web App.

     So the old filter did not describe a rule the system had. It just made
     every ADMIN unaddable — including one removing themselves from a team and
     then finding no way back in, which is how it was found. */
  const allAccessCodes = useMemo(
    () => new Set(allAccess.map((m) => m.UserCode)),
    [allAccess],
  );

  /* Every team folded? Derived rather than tracked, so opening one team by
     its own twisty flips the button back to "Collapse all" without anything
     having to remember to. */
  const allCollapsed =
    activeTeams.length > 0 && activeTeams.every((t) => collapsed[t.TeamCode]);

  /* Whoever sits above every team — the MD.
   *
   * Taken from the all-access accounts, since those are exactly the people who
   * hold every team by role rather than by membership, and read by
   * DesignationRank so the ladder decides rather than a name hard-coded here.
   * Ties break on UserCode so the chart does not reshuffle its own head
   * between loads.
   *
   * Null while no roster has landed, or when nobody has a designation yet —
   * the card falls back to the company name rather than guessing at one. */
  const topPerson = useMemo(() => {
    const ranked = allAccess
      .filter((m) => Number(m.DesignationRank ?? 999) < 999)
      .sort(
        (a, b) =>
          Number(a.DesignationRank) - Number(b.DesignationRank) ||
          Number(a.UserCode) - Number(b.UserCode),
      );
    return ranked[0] ?? allAccess[0] ?? null;
  }, [allAccess]);

  const peopleInTeams = useMemo(() => {
    const seen = new Set();
    activeTeams.forEach((t) =>
      membersOf(t.TeamCode).forEach((m) => seen.add(m.UserCode)),
    );
    return seen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeams, membersByTeam]);

  /* The same rule the Teams page's Add box uses, deliberately: the "No Team"
     chip counts the people that box would offer, and the two disagreeing about
     whether an ADMIN can hold a team is how you get a chip saying nobody is
     spare beside a list of people to add. */
  const unassigned = useMemo(
    () =>
      usersList.filter(
        (u) =>
          isActiveUser(u) &&
          !peopleInTeams.has(u.UserCode) &&
          !allAccessCodes.has(u.UserCode) &&
          String(u.Role || "").toUpperCase() !== "SUPER_ADMIN",
      ),
    [usersList, peopleInTeams, allAccessCodes],
  );

  const reload = () => {
    dispatch(teamListApis({}));
    activeTeams.forEach((t) =>
      dispatch(teamMembersApis({ teamCode: t.TeamCode })),
    );
  };

  /* The reporting lines, as a tree.
   *
   * This used to group people into BANDS by DesignationRank — everyone of one
   * grade in a row, the next grade in a row beneath. Two things were wrong
   * with it. DesignationRank is not a column any procedure returns, so every
   * member fell to the same default and every team drew exactly one band: a
   * flat row of names with a line to the team above them, which is a list, not
   * a chart. And even with the field populated a band cannot say that
   * Sivadhanushu is under Nitheeshkumar while Suthi is under Mohan — it can
   * only say they are the same grade, which is a different fact.
   *
   * ReportsTo says it exactly. Children are keyed by their manager; anyone
   * with no manager — or one who has since left the team — hangs off the team
   * card itself, so nobody can be dropped from the chart by a line pointing
   * somewhere that no longer exists.
   *
   * The cycle guard is not paranoia about the database, which refuses to write
   * one. It is about what a cycle costs HERE: recursion with no base case,
   * which is a hung tab rather than a wrong picture. Rows already in the table
   * from before that rule existed would find it. */
  const treeOf = (members) => {
    const present = new Set(members.map((m) => m.UserCode));
    const children = new Map();

    members.forEach((m) => {
      const parent =
        m.ReportsTo && present.has(m.ReportsTo) && m.ReportsTo !== m.UserCode
          ? m.ReportsTo
          : null;
      if (!children.has(parent)) children.set(parent, []);
      children.get(parent).push(m);
    });

    const seen = new Set();
    const build = (parent) =>
      (children.get(parent) || [])
        .filter((m) => !seen.has(m.UserCode))
        .map((m) => {
          seen.add(m.UserCode);
          return { member: m, reports: build(m.UserCode) };
        });

    const roots = build(null);

    /* Anyone the walk never reached is in a loop with their own manager.
       Shown at the top rather than silently dropped: a person missing from
       the chart is a bug nobody can see, and a person in the wrong place is
       one somebody will report. */
    const stranded = members
      .filter((m) => !seen.has(m.UserCode))
      .map((m) => ({ member: m, reports: [] }));

    return [...roots, ...stranded];
  };

  /* Just the card. The <li> around it belongs to renderNodes, which is
     what decides where in the tree this person hangs. */
  const memberCard = (m, team, colors) => (
    <OrgCard
      /* Leaves read lighter than the branches above them: three levels of
         identical solid plates gave the chart no hierarchy, and a page of
         saturated purple made the one thing you are looking for as loud as
         everything you are not. */
      variant="member"
      accent={colors.text}
      avatarColor={getAvatarColor(m.UserName)}
      title={m.UserName}
      /* Name and position. That is the whole card.
       *
       * It used to carry the email address underneath and a row of chips —
       * permission role, primary-team flag, open-task count — under that. Four
       * facts per card, on a chart with a dozen cards, none of them the
       * question a chart is read to answer. The email in particular is a
       * wrapping line of grey text that made every card twice as tall and told
       * a reader nothing they could not get from the Users page.
       *
       * The position is what an org chart is FOR. Blank when nobody has set
       * one, rather than falling back to the permission role: "User" under
       * somebody's name is not a job, and printing it there is how the chart
       * came to describe half the developers as Users. */
      meta={
        /* DesignationName, not Designation. sp_TeamMember_GetAll aliases the
           column as DesignationName, beside DesignationCode and
           DesignationRank — reading the wrong one here is why every card came
           up blank with the positions sitting in the database all along. */
        m.DesignationName ? (
          <span
            className={`org-chip org-chip--rank org-chip--rank-${Math.min(
              5,
              Math.max(1, Math.ceil((m.DesignationRank || 999) / 10)),
            )}`}
            title={`${m.UserName} — ${m.DesignationName}`}
          >
            {m.DesignationName}
          </span>
        ) : null
      }
      actions={null}
    />
  );

  /* One <li> per person, their reports in a <ul> beneath them.
   *
   * The pure-CSS org chart draws its connectors from the ul/li nesting, so the
   * shape of this markup IS the shape of the chart — nesting deeper is all it
   * takes to get another level of lines, at any depth. */
  const renderNodes = (nodes, team, colors) =>
    nodes.map(({ member, reports }) => (
      <li key={member.UserCode}>
        {memberCard(member, team, colors)}
        {reports.length > 0 && (
          <ul>{renderNodes(reports, team, colors)}</ul>
        )}
      </li>
    ));

  return (
    <div className="report-container p-2 p-md-4">
      {/* The header carries the FACTS about the page and nothing else. The
          controls used to sit up here too — three stat chips, a raw OS
          dropdown, a primary button and an icon button, five different shapes
          competing on one line, which is why "New Team" was wrapping onto two.
          Actions moved to the toolbar below, where they get room and an
          order. */}
      <PageHeader
        icon={<i className="bi bi-diagram-3-fill"></i>}
        title="Team Tree"
        subtitle="Who is in which team, and where work flows next"
        actions={
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <StatChip
              icon="bi-diagram-3"
              tone="purple"
              label="Teams"
              value={activeTeams.length}
            />
            <StatChip
              icon="bi-people-fill"
              tone="green"
              label="People"
              value={peopleInTeams.size}
            />
            {unassigned.length > 0 && (
              <StatChip
                icon="bi-person-dash"
                tone="orange"
                label="No Team"
                value={unassigned.length}
              />
            )}
          </div>
        }
      />

      {/* TOOLBAR — what you can DO, on its own line.

          The scope filter is a segmented control while the teams still fit on
          one row, and falls back to a select once they do not. Four teams read
          faster as four buttons than as a closed dropdown you have to open to
          see the options of; but this list grows, and a segmented control with
          a dozen segments is worse than a menu. */}
      <div className="tt-bar">
        <span className="tt-bar__legend">
          <i className="bi bi-funnel" />
          Showing
        </span>

        {activeTeams.length <= 5 ? (
          <div className="tt-seg" role="tablist" aria-label="Which teams to show">
            <button
              type="button"
              role="tab"
              aria-selected={teamFilter === "all"}
              className={`tt-seg__btn${teamFilter === "all" ? " is-active" : ""}`}
              onClick={() => setTeamFilter("all")}
            >
              All teams
              <span className="tt-seg__count">{activeTeams.length}</span>
            </button>
            {activeTeams.map((t) => {
              const on = String(teamFilter) === String(t.TeamCode);
              return (
                <button
                  key={t.TeamCode}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  className={`tt-seg__btn${on ? " is-active" : ""}`}
                  onClick={() => setTeamFilter(String(t.TeamCode))}
                  style={on ? { "--tt-seg-accent": getTeamColors(t.TeamName).text } : undefined}
                >
                  {t.TeamName}
                  <span className="tt-seg__count">
                    {membersOf(t.TeamCode).length}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <select
            className="tt-bar__select"
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            aria-label="Which teams to show"
          >
            <option value="all">All teams</option>
            {activeTeams.map((t) => (
              <option key={t.TeamCode} value={t.TeamCode}>
                {t.TeamName}
              </option>
            ))}
          </select>
        )}

        <div className="tt-bar__spacer" />

        {/* Collapse or expand every team at once.
         *
         * The chips beside this narrow the chart to ONE team, which answers
         * "show me Support" but not "show me the shape of the company" — for
         * that you want every team joined to the same head, just not every
         * name at once. Collapsing keeps the teams and their connectors and
         * folds only the people away, so the whole organisation fits on a
         * screen and any team can be opened again where it stands.
         *
         * The label reads as what pressing it will DO, not as the state it is
         * in: a button that says the current state leaves you guessing which
         * way it will go. */}
        <button
          className="tt-bar__btn"
          onClick={() =>
            setCollapsed(
              allCollapsed
                ? {}
                : Object.fromEntries(activeTeams.map((t) => [t.TeamCode, true])),
            )
          }
          title={
            allCollapsed
              ? "Show everybody in every team"
              : "Fold the people away and leave the teams"
          }
        >
          <i className={`bi bi-${allCollapsed ? "arrows-expand" : "arrows-collapse"} me-1`} />
          {allCollapsed ? "Expand all" : "Collapse all"}
        </button>

        <button
          className="tt-bar__btn tt-bar__btn--icon"
          onClick={reload}
          title="Reload"
          aria-label="Reload"
        >
          <i className="bi bi-arrow-clockwise" />
        </button>
      </div>

      {/* The tree reads; Teams writes.
          Shown to everybody, admins included, because for an admin the
          question is not "may I" but "where" — and the answer used to be a
          set of controls buried inside a canvas that pans sideways. */}
      <div className="tt-note tt-note--info mb-3">
        <i className="bi bi-eye" />
        <span>
          This is the picture of the organisation.{" "}
          {isAdminRole(role)
            ? "Creating teams and adding or removing people happens on the Teams page."
            : `You are signed in as ${role || "USER"}, so this is read-only — changing team membership is an admin action.`}
        </span>
        {isAdminRole(role) && (
          <button
            type="button"
            className="tt-bar__btn tt-bar__btn--primary ms-auto"
            onClick={() => navigate("/teams")}
          >
            <i className="bi bi-pencil-square" />
            Edit on Teams
          </button>
        )}
      </div>


      <div className="position-relative" style={{ minHeight: "260px" }}>
        {(teamsStatus === "loading" || busy || saveStatus === "loading") && (
          <div className="tt-overlay">
            <Loader isLoading={true} />
          </div>
        )}

        <div className="org-scroll">
          <div className="org">
            <ul>
              {/* ROOT — the organisation. Gives the teams a single parent to
                  hang from; without it four teams would float unconnected. */}
              <li>
                <OrgCard
                  accent="#4f46e5"
                  icon={topPerson ? undefined : "bi-buildings-fill"}
                  avatarColor={
                    topPerson ? getAvatarColor(topPerson.UserName) : undefined
                  }
                  title={topPerson ? topPerson.UserName : "SkyEye"}
                  sub={
                    topPerson
                      ? topPerson.DesignationName || formatRole(topPerson.Role)
                      : `${activeTeams.length} teams · ${peopleInTeams.size} people`
                  }
                  meta={
                    <span className="org-chip org-chip--count">
                      {activeTeams.length} teams · {peopleInTeams.size} people
                    </span>
                  }
                />

                {/* Guarded: an empty <ul> still draws its drop-line from the
                    parent, so on first paint — before /team/list answers — the
                    root card grew a stub connecting it to nothing. */}
                {shownTeams.length > 0 && (
                <ul>
                  {shownTeams.map((team) => {
                    const members = membersOf(team.TeamCode);
                    const routes = flowByTeam[team.TeamCode] || [];
                    const colors = getTeamColors(team.TeamName);
                    const isShut = !!collapsed[team.TeamCode];

                    return (
                      <li
                        key={team.TeamCode}
                        className={isShut ? "is-collapsed" : ""}
                      >
                        <OrgCard
                          variant="team"
                          accent={colors.text}
                          icon="bi-people-fill"
                          title={team.TeamName}
                          sub={
                            routes.length
                              ? undefined
                              : "no onward routes configured"
                          }
                          meta={
                            <>
                              <span className="org-chip org-chip--count">
                                {members.length}{" "}
                                {members.length === 1 ? "member" : "members"}
                              </span>
                              {routes.map((r) => (
                                <span
                                  key={r.ToTeamCode}
                                  className={`org-chip ${
                                    r.FlowType === "Return"
                                      ? "org-chip--back"
                                      : "org-chip--fwd"
                                  }`}
                                  title={
                                    r.FlowType === "Return"
                                      ? `Can send work back to ${r.ToTeamName}`
                                      : `Passes work on to ${r.ToTeamName}`
                                  }
                                >
                                  {r.FlowType === "Return" ? "↩ " : "→ "}
                                  {r.ShortName || r.ToTeamName}
                                </span>
                              ))}
                            </>
                          }
                          actions={null}
                          twisty={
                            members.length > 0 ? (
                              <button
                                type="button"
                                className="org-twisty"
                                aria-expanded={!isShut}
                                title={isShut ? "Show members" : "Hide members"}
                                onClick={() =>
                                  setCollapsed((p) => ({
                                    ...p,
                                    [team.TeamCode]: !p[team.TeamCode],
                                  }))
                                }
                              >
                                <i
                                  className={`bi bi-${isShut ? "plus" : "dash"}-lg`}
                                />
                              </button>
                            ) : null
                          }
                        />

                        <ul>
                          {members.length === 0 ? (
                            <li>
                              <div className="org-empty-card">
                                <i className="bi bi-person-slash d-block mb-1" />
                                Nobody here yet. Work routed to{" "}
                                {team.TeamName} will sit unclaimed.
                              </div>
                            </li>
                          ) : (
                            renderNodes(treeOf(members), team, colors)
                          )}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
                )}
              </li>
            </ul>
          </div>
        </div>

        {/* People in no team at all. Kept out of the chart because they hang
            off nothing — and easy to miss when the chart only ever shows who
            IS somewhere. */}
        {unassigned.length > 0 && (
          <div className="tt-note tt-note--warn mt-3">
            <i className="bi bi-person-dash-fill" />
            <span>
              <strong>
                {unassigned.length}{" "}
                {unassigned.length === 1 ? "person is" : "people are"} in no
                team
              </strong>{" "}
              — {unassigned.map((u) => u.UserName).join(", ")}. They cannot be
              given team work until assigned above.
            </span>
          </div>
        )}

        {/* Said once, rather than repeated inside all four teams.
            fn_UserVisibleTeams grants every team to these roles, so they can
            act on any task without holding a membership row — and a chart that
            listed them four times would imply four memberships somebody could
            remove. */}
        {allAccess.length > 0 && (
          <div className="tt-note tt-note--all mt-3">
            <i className="bi bi-key-fill" />
            <span>
              <strong>Every team</strong> is also reachable by{" "}
              {allAccess.map((m) => m.UserName).join(", ")} — admins and team
              leaders hold all teams by role, so they are not listed as members
              of any one of them.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamTree;
